// @flow
import * as React from 'react'
import BrandLogo from 'icon/BrandLogo'
import type { User } from 'types'
import Link from 'ui/Link'
import { adminUrl, homeUrl, partnerUrl, visionUrl } from 'urls'
import { BasePageHeader, Layout, LayoutItem, type LinkManifest } from '../../common'
import { type Filters } from '..'
import SearchBar from './SearchBar'
import { Button } from 'ui/Form'
import { WhitelabelContext } from 'main/framework'
import { AssetName } from 'main/util/asset'
import { spacing } from 'main/ui'

import './PageHeader.module.scss'
import { TFunction, withTranslation } from 'react-i18next'
import { LOCIZE_NAMESPACE } from 'main/common'

const PRE = `HEADER-DESKTOP`

type Props = {
  user: User | null,
  linkManifests: LinkManifest[],
  filters: Filters,
  onUpdateFilters: (filters: $Shape<Filters>) => void,
  onClearFilters: () => void,
  onSearch: () => void,
  onRequestLogin: () => void,
  onRequestSignUp: () => void,
  t: TFunction,
}

class MarcomPageHeaderDesktop extends React.Component<Props> {
  static contextType = WhitelabelContext

  render = () => {
    return (
      <BasePageHeader linkManifests={this.props.linkManifests}>
        {this.props.user ? this.renderLoggedInContent() : this.renderLoggedOutContent()}
      </BasePageHeader>
    )
  }

  renderLoggedInContent = () => {
    return (
      <Layout>
        <LayoutItem>{this.renderBrand()}</LayoutItem>
        <LayoutItem grow>{this.renderSearchBar()}</LayoutItem>
        <LayoutItem>{this.renderQuickLinks()}</LayoutItem>
      </Layout>
    )
  }

  renderLoggedOutContent = () => {
    return (
      <Layout>
        <LayoutItem>{this.renderBrand()}</LayoutItem>
        <LayoutItem grow>{this.renderSearchBar()}</LayoutItem>
        <LayoutItem>{this.renderQuickLinks()}</LayoutItem>
      </Layout>
    )
  }

  linkOrAtag = (linkUrl: any, whitelabelLinkUrl: any, guts: any) => {
    return whitelabelLinkUrl ? (
      <a href={whitelabelLinkUrl}>{guts}</a>
    ) : (
      <Link native href={linkUrl}>
        {guts}
      </Link>
    )
  }

  renderBrand = () => {
    const whitelabelLogo = this.context.getAsset(AssetName.LOGO)
    const whitelabelLinkUrl = this.context.getAsset(AssetName.LOGO_LINK_URL)

    const guts = whitelabelLogo ? (
      this.renderWhitelabeledLogo(whitelabelLogo)
    ) : (
      <BrandLogo layout="heightConstrained" />
    )

    return <div styleName="brand">{this.linkOrAtag(homeUrl(), whitelabelLinkUrl, guts)}</div>
  }

  renderWhitelabeledLogo = (whitelabelLogo: any) => {
    return (
      <img
        src={`${whitelabelLogo}-/resize/x50/-/quality/smart/`}
        style={{ marginTop: `-${spacing.minorScale(4)}px` }}
      />
    )
  }

  renderSearchBar = () => {
    return (
      <div styleName="searchBar">
        <SearchBar
          filters={this.props.filters}
          onUpdateFilters={this.props.onUpdateFilters}
          onClearFilters={this.props.onClearFilters}
          onSearch={this.props.onSearch}
          placeholder={this.props.t(`${PRE}-SEARCH`)}
        />
      </div>
    )
  }

  renderQuickLinks = () => {
    const user = this.props.user
    const isDirector = user && user.schoolhouse && !user.schoolhouse.closedAt
    let renderContextAwareQuickLinks

    if (!user) renderContextAwareQuickLinks = this.renderAnonymousQuickLinks
    else if (isDirector) renderContextAwareQuickLinks = this.renderDirectorQuickLinks
    else renderContextAwareQuickLinks = this.renderParentQuickLinks.bind(this)

    return <div styleName="quickLinks">{renderContextAwareQuickLinks()}</div>
  }

  renderAnonymousQuickLinks = () => {
    const { t } = this.props

    return (
      <React.Fragment>
        <QuickLink context="muted" href={visionUrl()}>
          {t(`${PRE}-SAFETY`)}
        </QuickLink>
        <QuickLink context="muted" href={partnerUrl()}>
          {t(`${PRE}-START`)}
        </QuickLink>
        <QuickLink context="muted" onClick={this.props.onRequestLogin}>
          {t(`${PRE}-LOGIN`)}
        </QuickLink>
        <div styleName="signUpButton">
          <Button
            appearance="outlineBlue"
            onClick={this.props.onRequestSignUp}
            data-pendo="signUpCta"
          >
            {t(`${PRE}-SIGNUP`)}
          </Button>
        </div>
      </React.Fragment>
    )
  }

  renderParentQuickLinks = () => {
    const { t } = this.props
    return (
      <React.Fragment>
        <QuickLink context="muted" href="/family/programs" native>
          {t(`${PRE}-MY-PROGRAMS`)}
        </QuickLink>
        <QuickLink context="muted" href="/messages" native>
          {t(`${PRE}-MESSAGES`)}
        </QuickLink>
      </React.Fragment>
    )
  }

  renderDirectorQuickLinks = () => {
    const { t } = this.props

    return (
      <QuickLink context="pronounced" href={adminUrl()}>
        {t(`${PRE}-PROGRAM`)}
      </QuickLink>
    )
  }

  renderLogInQuickLinkMaybe = () => {
    if (this.props.user) return
    const { t } = this.props

    return (
      <QuickLink onClick={this.props.onRequestLogin} data-pendo="signUpCta">
        {t(`${PRE}-CTA`)}
      </QuickLink>
    )
  }
}

type QuickLinkProps = {
  context?: 'muted' | 'pronounced',
  href?: string,
  onClick?: (event?: Event) => void,
  children: React.Node,
  native?: boolean,
  'data-pendo'?: string,
}

function QuickLink(props: QuickLinkProps) {
  return (
    <div styleName={`quickLink ${props.context || ''}`} data-pendo={props['data-pendo']}>
      <Link blend href={props.href} onClick={props.onClick} native={props.native}>
        {props.children}
      </Link>
    </div>
  )
}

export default withTranslation(LOCIZE_NAMESPACE)(MarcomPageHeaderDesktop)
